/**
 * Programme ambassadeur (04/11/2026) — « Ma page sur le site » du formateur :
 *  - vues de chacune de ses formations publiées sur skills4mation.com (hier, 7 jours, 30 jours, total ; historique
 *    quotidien en courbe), chiffres arrêtés à la veille, mesure anonyme ;
 *  - « Demandes reçues via ma page » : les demandes relayées par l'organisme (services/leads-formateur.ts) ;
 *  - retrait de l'autorisation d'affichage (nom et photo) et de l'adhésion au relais (04/11/2026), confirmés dans une
 *    boîte de dialogue ; l'organisme est prévenu et republie la fiche.
 * `CourbeVues` et `ResumeVues` servent aussi sur la fiche d'une formation (carte « Publication sur le site »).
 */
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, Eye, Inbox, Mail, Phone, ShieldOff } from "lucide-react";
import { api, instantFr, type LeadFormateur, type StatistiquesVues } from "../api";
import {
  Alerte,
  Bouton,
  Carte,
  Chargement,
  EtatVide,
  Indicateur,
  Modale,
  TitrePage,
  useNotifier,
  useTitreDocument,
} from "../ui/base";

type LigneVues = StatistiquesVues["formations"][number];

const nombre = (n: number) => n.toLocaleString("fr-FR");
const jourCourt = (j: string) =>
  new Date(`${j}T12:00:00Z`).toLocaleDateString("fr-FR", { day: "numeric", month: "short", timeZone: "UTC" });
const jourLong = (j: string) =>
  new Date(`${j}T12:00:00Z`).toLocaleDateString("fr-FR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    timeZone: "UTC",
  });

/**
 * Courbe de l'historique quotidien : une série (pas de légende, le titre la nomme), trait 2 px aux couleurs de la
 * charte (jetons CSS, donc lisible dans le thème clair comme dans un thème sombre), grille discrète, réticule et
 * info-bulle au survol ou au clavier, tableau des valeurs pour les lecteurs d'écran.
 */
export function CourbeVues({ historique, titre }: { historique: LigneVues["historique"]; titre: string }) {
  const id = useId();
  const [survol, setSurvol] = useState<number | null>(null);
  // La largeur du dessin suit celle du conteneur : le texte garde sa taille réelle (11 px), même à 390 px.
  const cadre = useRef<HTMLDivElement>(null);
  const [L, setL] = useState(640);
  useEffect(() => {
    const el = cadre.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const o = new ResizeObserver(([e]) => setL(Math.max(260, Math.round(e!.contentRect.width))));
    o.observe(el);
    return () => o.disconnect();
  }, []);
  const H = L < 480 ? 160 : 190;
  const m = { g: 34, d: 10, h: 12, b: 26 };
  const max = Math.max(4, ...historique.map((p) => p.vues));
  const pas = Math.pow(10, Math.floor(Math.log10(max)));
  const plafond = Math.ceil(max / pas) * pas;
  const x = (i: number) => m.g + (i * (L - m.g - m.d)) / Math.max(1, historique.length - 1);
  const y = (v: number) => H - m.b - (v * (H - m.h - m.b)) / plafond;
  const trace = historique
    .map((p, i) => `${i ? "L" : "M"}${x(i).toFixed(1)},${y(p.vues).toFixed(1)}`)
    .join("");
  const aire = `${trace}L${x(historique.length - 1).toFixed(1)},${y(0)}L${x(0).toFixed(1)},${y(0)}Z`;
  const graduations = [0, plafond / 2, plafond];
  const reperes = historique
    .map((p, i) => ({ p, i }))
    .filter(({ i }) => i % (L < 480 ? 30 : 15) === (historique.length - 1) % (L < 480 ? 30 : 15));
  const point = survol === null ? null : historique[survol]!;
  const choisir = (clientX: number, rect: DOMRect) => {
    const r = ((clientX - rect.left) / rect.width) * L;
    setSurvol(
      Math.min(
        historique.length - 1,
        Math.max(0, Math.round(((r - m.g) / (L - m.g - m.d)) * (historique.length - 1))),
      ),
    );
  };
  return (
    <figure className="mt-3">
      <figcaption id={`${id}-t`} className="text-[13px] font-medium text-encre-2">
        {titre}
      </figcaption>
      <div className="relative mt-2" ref={cadre}>
        <svg
          viewBox={`0 0 ${L} ${H}`}
          className="block h-auto w-full touch-pan-y text-accent outline-none focus-visible:ring-2 focus-visible:ring-focus"
          role="img"
          aria-labelledby={`${id}-t`}
          tabIndex={0}
          onPointerMove={(e) => choisir(e.clientX, e.currentTarget.getBoundingClientRect())}
          onPointerLeave={() => setSurvol(null)}
          onKeyDown={(e) => {
            if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
              e.preventDefault();
              setSurvol((s) =>
                Math.min(
                  historique.length - 1,
                  Math.max(0, (s ?? historique.length - 1) + (e.key === "ArrowLeft" ? -1 : 1)),
                ),
              );
            }
          }}
          onBlur={() => setSurvol(null)}
        >
          {graduations.map((g) => (
            <g key={g}>
              <line
                x1={m.g}
                x2={L - m.d}
                y1={y(g)}
                y2={y(g)}
                stroke="var(--color-trait)"
                strokeWidth={1}
                strokeDasharray={g === 0 ? undefined : "2 4"}
              />
              <text
                x={m.g - 6}
                y={y(g) + 4}
                textAnchor="end"
                fontSize={11}
                fill="var(--color-encre-3)"
                className="chiffres"
              >
                {nombre(g)}
              </text>
            </g>
          ))}
          {reperes.map(({ p, i }) => (
            <text
              key={p.jour}
              x={x(i)}
              y={H - 8}
              textAnchor={i === 0 ? "start" : i === historique.length - 1 ? "end" : "middle"}
              fontSize={11}
              fill="var(--color-encre-3)"
            >
              {jourCourt(p.jour)}
            </text>
          ))}
          <path d={aire} fill="currentColor" opacity={0.1} />
          <path
            d={trace}
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinejoin="round"
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
          />
          {point && survol !== null && (
            <g>
              <line
                x1={x(survol)}
                x2={x(survol)}
                y1={m.h}
                y2={y(0)}
                stroke="var(--color-encre-3)"
                strokeWidth={1}
              />
              <circle
                cx={x(survol)}
                cy={y(point.vues)}
                r={4.5}
                fill="currentColor"
                stroke="var(--color-carte)"
                strokeWidth={2}
              />
            </g>
          )}
        </svg>
        {point && survol !== null && (
          <div
            className="pointer-events-none absolute top-0 z-10 -translate-x-1/2 rounded-md border border-trait bg-carte px-2.5 py-1.5 text-xs whitespace-nowrap text-encre shadow-flottant"
            style={{ left: `${Math.min(88, Math.max(12, (x(survol) / L) * 100))}%` }}
            role="status"
          >
            <span className="block text-encre-3 first-letter:uppercase">{jourLong(point.jour)}</span>
            <span className="chiffres font-semibold">
              {nombre(point.vues)} vue{point.vues > 1 ? "s" : ""}
            </span>
          </div>
        )}
      </div>
      <details className="mt-1 text-xs text-encre-2">
        <summary className="cursor-pointer select-none">Voir les valeurs jour par jour</summary>
        <div className="mt-2 max-h-56 overflow-auto rounded-md border border-trait">
          <table className="w-full text-left">
            <thead className="sticky top-0 bg-papier-2">
              <tr>
                <th className="px-2 py-1 font-medium">Jour</th>
                <th className="px-2 py-1 text-right font-medium">Vues</th>
              </tr>
            </thead>
            <tbody>
              {[...historique].reverse().map((p) => (
                <tr key={p.jour} className="border-t border-trait">
                  <td className="px-2 py-1">{jourLong(p.jour)}</td>
                  <td className="chiffres px-2 py-1 text-right">{nombre(p.vues)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </figure>
  );
}

export function ResumeVues({ f }: { f: LigneVues }) {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      <Indicateur libelle="Hier" valeur={nombre(f.hier)} />
      <Indicateur libelle="7 derniers jours" valeur={nombre(f.sept_jours)} />
      <Indicateur libelle="30 derniers jours" valeur={nombre(f.trente_jours)} />
      <Indicateur
        libelle="Total"
        valeur={nombre(f.total)}
        detail={
          f.acceptee_le
            ? `acceptée le ${new Date(f.acceptee_le).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}`
            : undefined
        }
      />
    </div>
  );
}

const RETRAITS = {
  image: {
    bouton: "Retirer mon autorisation d'affichage",
    titre: "Retirer votre nom et votre photo de cette page ?",
    texte:
      "Votre autorisation d'afficher votre nom et votre photo sur cette page est retirée dès maintenant. L'organisme est prévenu et republie la page sans le bloc « Votre formateur » (sous 48 h). L'accord que vous aviez donné reste conservé, daté, avec ce retrait.",
    succes: "Autorisation retirée : l'organisme va republier la page sans votre nom ni votre photo.",
  },
  relais: {
    bouton: "Ne plus recevoir les demandes",
    titre: "Quitter le relais des demandes pour cette page ?",
    texte:
      "Les prochaines demandes envoyées depuis cette page ne vous seront plus transmises : l'organisme les traitera. Les demandes déjà reçues restent visibles ci-dessous. L'organisme est prévenu et republie la page sans le formulaire du programme ambassadeur.",
    succes: "Relais arrêté : les prochaines demandes resteront chez l'organisme.",
  },
} as const;

/** Autorisations données à la publication, et leur retrait (formateur seulement, fiche issue d'une demande acceptée). */
function Autorisations({ f }: { f: LigneVues }) {
  const requetes = useQueryClient();
  const notifier = useNotifier();
  const [confirmer, setConfirmer] = useState<keyof typeof RETRAITS | null>(null);
  const retirer = useMutation({
    mutationFn: (quoi: keyof typeof RETRAITS) => api.post(`/publications/${f.demande_id}/retrait/${quoi}`),
    onSuccess: async (_r, quoi) => {
      setConfirmer(null);
      notifier("succes", RETRAITS[quoi].succes);
      await requetes.invalidateQueries({ queryKey: ["vues-fiches"] });
    },
    onError: (e) => notifier("danger", e.message),
  });
  if (!f.demande_id || (f.affichage === "aucune" && f.relais === "aucune")) return null;
  const ligne = (quoi: keyof typeof RETRAITS, etat: LigneVues["affichage"], actif: string, retire: string) =>
    etat === "aucune" ? null : (
      <li className="flex flex-wrap items-center justify-between gap-2 py-2">
        <span className="text-sm text-encre-2">{etat === "active" ? actif : retire}</span>
        {etat === "active" && (
          <Bouton
            variante="discret"
            taille="sm"
            icone={<ShieldOff className="size-4" aria-hidden />}
            onClick={() => setConfirmer(quoi)}
          >
            {RETRAITS[quoi].bouton}
          </Bouton>
        )}
      </li>
    );
  return (
    <div className="mt-4 border-t border-trait pt-3" data-bloc="autorisations">
      <p className="text-[13px] font-medium text-encre-2">Vos autorisations sur cette page</p>
      <ul className="divide-y divide-trait">
        {ligne(
          "image",
          f.affichage,
          "Votre nom et votre photo sont affichés (bloc « Votre formateur »).",
          "Autorisation d'affichage retirée : la page est republiée sans votre nom ni votre photo.",
        )}
        {ligne(
          "relais",
          f.relais,
          "Les demandes reçues via cette page vous sont transmises.",
          "Relais arrêté : les demandes restent chez l'organisme.",
        )}
      </ul>
      <Modale
        ouverte={confirmer !== null}
        fermer={() => setConfirmer(null)}
        titre={confirmer ? RETRAITS[confirmer].titre : ""}
      >
        {confirmer && (
          <>
            <p className="text-sm text-encre-2">{RETRAITS[confirmer].texte}</p>
            <div className="mt-5 flex flex-wrap justify-end gap-2">
              <Bouton variante="secondaire" onClick={() => setConfirmer(null)}>
                Annuler
              </Bouton>
              <Bouton
                variante="danger"
                icone={<ShieldOff className="size-4" aria-hidden />}
                enCours={retirer.isPending}
                onClick={() => retirer.mutate(confirmer)}
              >
                Confirmer le retrait
              </Bouton>
            </div>
          </>
        )}
      </Modale>
    </div>
  );
}

function CarteVues({ f, arrete }: { f: LigneVues; arrete: string }) {
  return (
    <Carte className="p-5" data-carte="vues">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <h3 className="min-w-0 text-base font-semibold">{f.titre}</h3>
        <a
          href={f.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-[13px] font-medium text-accent hover:underline"
        >
          Voir la page <ExternalLink className="size-3.5" aria-hidden />
        </a>
      </div>
      <p className="mt-0.5 text-xs text-encre-3">
        Chiffres arrêtés au {jourLong(arrete)} · mis à jour chaque jour
      </p>
      <div className="mt-3">
        <ResumeVues f={f} />
      </div>
      <CourbeVues historique={f.historique} titre={`Vues par jour — ${f.historique.length} derniers jours`} />
      <Autorisations f={f} />
    </Carte>
  );
}

function CarteLead({ l }: { l: LeadFormateur }) {
  return (
    <li className="rounded-md border border-trait bg-carte p-4 shadow-carte">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="font-semibold">{l.anonymise ? "Demande anonymisée" : `${l.prenom} ${l.nom}`.trim()}</p>
        <p className="text-xs text-encre-3">
          {instantFr(l.cree_le)} · n° {l.numero}
        </p>
      </div>
      <p className="mt-0.5 text-[13px] text-encre-2">{l.sujet}</p>
      {!l.anonymise && (
        <p className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm">
          <a
            href={`mailto:${l.email}`}
            className="inline-flex min-w-0 items-center gap-1.5 break-all text-accent hover:underline"
          >
            <Mail className="size-3.5 shrink-0" aria-hidden /> {l.email}
          </a>
          {l.telephone && (
            <a
              href={`tel:${l.telephone.replace(/\s/g, "")}`}
              className="inline-flex items-center gap-1.5 text-accent hover:underline"
            >
              <Phone className="size-3.5" aria-hidden /> {l.telephone}
            </a>
          )}
        </p>
      )}
      {(l.objectif || l.message) && (
        <p className="mt-2 text-sm whitespace-pre-line text-encre">
          {[l.objectif, l.message].filter(Boolean).join("\n")}
        </p>
      )}
      {l.fiche_url && (
        <p className="mt-2 text-xs text-encre-3">
          Via{" "}
          <a
            href={l.fiche_url}
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-accent"
          >
            la page de la formation
          </a>
        </p>
      )}
    </li>
  );
}

export function MaVitrine() {
  useTitreDocument("Ma page sur le site");
  const vues = useQuery({
    queryKey: ["vues-fiches"],
    queryFn: () => api.get<StatistiquesVues>("/formateur/vues"),
  });
  const leads = useQuery({
    queryKey: ["demandes-recues"],
    queryFn: () => api.get<LeadFormateur[]>("/formateur/demandes-recues"),
  });
  const totalVues = useMemo(
    () => (vues.data?.formations ?? []).reduce((n, f) => n + f.total, 0),
    [vues.data],
  );
  return (
    <div className="space-y-8">
      <TitrePage
        titre="Ma page sur le site"
        soustitre="Programme ambassadeur : l'audience de vos formations publiées sur skills4mation.com et les demandes qui vous sont relayées."
      />

      <section aria-labelledby="titre-vues" className="space-y-3">
        <h2 id="titre-vues" className="flex items-center gap-2 text-lg font-semibold">
          <Eye className="size-4.5 text-accent" aria-hidden /> Vues de mes pages{" "}
          {vues.data && vues.data.formations.length > 1 && (
            <span className="chiffres text-sm font-normal text-encre-3">· {nombre(totalVues)} au total</span>
          )}
        </h2>
        {vues.isPending && <Chargement />}
        {vues.error && <Alerte ton="danger">{vues.error.message}</Alerte>}
        {vues.data && vues.data.formations.length === 0 && (
          <EtatVide
            icone={<Eye className="size-5" aria-hidden />}
            titre="Aucune formation publiée pour l'instant"
          >
            Demandez la publication d'une formation depuis sa fiche (« Publication sur le site ») : ses vues
            s'afficheront ici dès sa mise en ligne.
          </EtatVide>
        )}
        {vues.data?.formations.map((f) => (
          <CarteVues key={f.slug} f={f} arrete={vues.data.arrete_au} />
        ))}
        <p className="text-xs text-encre-3">
          Mesure anonyme : ni cookie, ni adresse IP, ni identifiant ; une vue par visiteur et par page sur 30
          minutes, robots exclus.
        </p>
      </section>

      <section id="demandes" aria-labelledby="titre-demandes" className="scroll-mt-24 space-y-3">
        <h2 id="titre-demandes" className="flex items-center gap-2 text-lg font-semibold">
          <Inbox className="size-4.5 text-accent" aria-hidden /> Demandes reçues via ma page
        </h2>
        {leads.isPending && <Chargement />}
        {leads.error && <Alerte ton="danger">{leads.error.message}</Alerte>}
        {leads.data && leads.data.length === 0 && (
          <EtatVide icone={<Inbox className="size-5" aria-hidden />} titre="Aucune demande pour l'instant">
            Quand une personne écrit depuis la page d'une formation pour laquelle vous avez adhéré au
            programme ambassadeur, sa demande apparaît ici et vous êtes prévenu par e-mail.
          </EtatVide>
        )}
        {leads.data && leads.data.length > 0 && (
          <ul className="space-y-3">
            {leads.data.map((l) => (
              <CarteLead key={l.id} l={l} />
            ))}
          </ul>
        )}
        {leads.data && leads.data.length > 0 && (
          <p className="text-xs text-encre-3">
            Ces coordonnées vous sont transmises pour répondre à la demande, et à cette seule fin (RGPD).
          </p>
        )}
      </section>
    </div>
  );
}
